/**
 * RecipeLineList Component
 *
 * Displays a table/list of recipe ingredients with edit/delete actions.
 * Shows ingredient details including quantity, unit, and cost information.
 *
 * US-P2-010: Process Recipe Integration - Task 3
 */

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { PER_UNIT_LABELS } from '@/hooks/useRecipeTemplates'
import {
  Pencil,
  Trash2,
  Package,
  AlertTriangle
} from 'lucide-react'

/**
 * Format quantity with appropriate precision
 */
function formatQuantity(quantity, unit) {
  if (quantity === null || quantity === undefined) return '-'

  const num = Number(quantity)

  // Use appropriate decimal places based on unit
  if (unit === 'g' || unit === 'ml') {
    return num.toFixed(0)
  } else if (num >= 1) {
    return num.toFixed(2).replace(/\.?0+$/, '')
  } else {
    return num.toFixed(3).replace(/\.?0+$/, '')
  }
}

/**
 * Single recipe line row
 */
function RecipeLineRow({
  line,
  onEdit,
  onDelete,
  readOnly = false,
  saving = false
}) {
  const itemName = line.inventory_item?.name || line.item_name || 'Unknown Item'
  const itemCategory = line.inventory_item?.category || ''

  return (
    <TableRow className="group">
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium">{itemName}</div>
            {itemCategory && (
              <div className="text-xs text-muted-foreground">{itemCategory}</div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right">
        {formatQuantity(line.quantity, line.unit)} {line.unit}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {PER_UNIT_LABELS[line.per_unit] || line.per_unit}
        </Badge>
      </TableCell>
      <TableCell>
        {line.is_optional ? (
          <Badge variant="secondary" className="text-xs">
            Optional
          </Badge>
        ) : (
          <Badge variant="default" className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
            Required
          </Badge>
        )}
      </TableCell>
      <TableCell className="max-w-[200px]">
        {line.notes ? (
          <span className="text-sm text-muted-foreground truncate block" title={line.notes}>
            {line.notes}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      {!readOnly && (
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onEdit(line)}
              disabled={saving}
              title="Edit ingredient"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => onDelete(line)}
              disabled={saving}
              title="Remove ingredient"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  )
}

/**
 * RecipeLineList - Table of recipe ingredients
 *
 * @param {Array} lines - Array of recipe line objects
 * @param {function} onEdit - Callback when edit button clicked
 * @param {function} onDelete - Callback when delete button clicked
 * @param {boolean} readOnly - Whether the list is read-only (no edit/delete)
 * @param {boolean} saving - Whether a save operation is in progress
 */
export default function RecipeLineList({
  lines = [],
  onEdit,
  onDelete,
  readOnly = false,
  saving = false
}) {
  if (lines.length === 0) {
    return (
      <div className="py-8 text-center border border-dashed rounded-lg">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-semibold mb-2">No ingredients defined</h3>
        <p className="text-sm text-muted-foreground">
          Add ingredients to define this recipe
        </p>
      </div>
    )
  }

  // Check for items without inventory_item data (orphaned references)
  const hasOrphanedItems = lines.some(line => !line.inventory_item && !line.item_name)

  return (
    <div className="space-y-3">
      {hasOrphanedItems && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Some ingredients reference items that may no longer exist in inventory</span>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ingredient</TableHead>
              <TableHead className="text-right w-[120px]">Quantity</TableHead>
              <TableHead className="w-[100px]">Scale</TableHead>
              <TableHead className="w-[80px]">Status</TableHead>
              <TableHead>Notes</TableHead>
              {!readOnly && <TableHead className="w-[80px]"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line, index) => (
              <RecipeLineRow
                key={line.id || index}
                line={line}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                readOnly={readOnly}
                saving={saving}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
        <span>
          {lines.length} ingredient{lines.length !== 1 ? 's' : ''}
        </span>
        <span>
          {lines.filter(l => !l.is_optional).length} required,{' '}
          {lines.filter(l => l.is_optional).length} optional
        </span>
      </div>
    </div>
  )
}
